import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatBusinessDate } from '@/lib/format';
import { supabase } from '@/integrations/supabase/client';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ListPagination } from '@/components/ListPagination';
import { Skeleton } from '@/components/ui/skeleton';
import { CurrencyDisplay } from '@/components/ui/stat-card';
import { Badge } from '@/components/ui/badge';

interface AllocationRecord {
    repayment_id: number;
    allocation_amount: number;
    payment_amount: number;
    payment_date: string;
    payment_description: string;
    total_count: number;
}

interface LoanAllocationSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    contractId: number;
    loanId: number;
    loanType: 'LOAN' | 'INSURANCE_LOAN';
    title: string;
}

const PAGE_SIZE = 10;

export function LoanAllocationSheet({
    open,
    onOpenChange,
    contractId,
    loanId,
    loanType,
    title,
}: LoanAllocationSheetProps) {
    const [currentPage, setCurrentPage] = useState(1);

    const { data, isLoading } = useQuery({
        queryKey: ['loan-allocations', contractId, loanId, loanType, currentPage],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_loan_allocations', {
                p_contract_id: contractId,
                p_loan_id: loanId,
                p_loan_type: loanType,
                p_offset: (currentPage - 1) * PAGE_SIZE,
                p_limit: PAGE_SIZE,
            });

            if (error) throw error;
            return (data || []) as AllocationRecord[];
        },
        enabled: open && !!contractId && !!loanId,
    });

    const totalCount = data?.[0]?.total_count || 0;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                hideOverlay
                className="w-[500px] m-4 h-[calc(100vh-32px)] rounded-xl border flex flex-col"
            >
                <SheetHeader className="mb-4">
                    <SheetTitle>Allocation History - {title}</SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto -mr-6 pr-6">
                    {isLoading ? (
                        <div className="space-y-4">
                            <Skeleton className="h-8 w-full" />
                            <Skeleton className="h-8 w-full" />
                            <Skeleton className="h-8 w-full" />
                        </div>
                    ) : data && data.length > 0 ? (
                        <div className="space-y-4">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Payment</TableHead>
                                        <TableHead>Allocated</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.map((alloc) => (
                                        <TableRow key={alloc.repayment_id}>
                                            <TableCell className="text-muted-foreground">
                                                {formatBusinessDate(alloc.payment_date)}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-col">
                                                    <CurrencyDisplay amount={alloc.payment_amount} />
                                                    {alloc.payment_description && (
                                                        <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                                                            {alloc.payment_description}
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant="outline"
                                                    className={
                                                        loanType === 'LOAN'
                                                            ? "text-blue-600 bg-blue-50 border-blue-100 font-mono"
                                                            : "text-amber-600 bg-amber-50 border-amber-100 font-mono"
                                                    }
                                                >
                                                    →<CurrencyDisplay amount={alloc.allocation_amount} />
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            <div className="flex justify-center pb-4">
                                <ListPagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                    totalItems={totalCount}
                                />
                            </div>
                        </div>
                    ) : (
                        <p className="text-center text-muted-foreground py-8">
                            No allocations found for this advance.
                        </p>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}
